import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { createPortal } from 'react-dom';

interface AccountDisableModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  reason?: string; // Add reason prop to handle specific deactivation reasons
}

export function AccountDisableModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  reason,
}: AccountDisableModalProps) {
  const { i18n } = useTranslation();

  const getModalContent = () => {
    // Customize message based on reason
    let customMessage = message;
    if (!customMessage && reason === 'deactivated_by_admin') {
      customMessage =
        i18n.language === 'mm'
          ? 'သင့် Merchant အကောင့်ကို အက်ဒမင်မှ ပိတ်ထားပါသည်။ အကူအညီအတွက် ကျေးဇူးပြု၍ ဝန်ဆောင်မှုအဖွဲ့ကို ဆက်သွယ်ပါ။\n\nသင့်အသုံးပြုသူအကောင့်သို့ ပြန်သွားရန် Back to User ကိုနှိပ်ပါ။'
          : 'Your merchant account has been deactivated by the administrator. Please contact support for assistance.\n\nClick Back to User to return to your user account.';
    }

    return {
      icon: <AlertTriangle className="w-8 h-8 text-red-600" />,
      title: title || (i18n.language === 'mm' ? 'အကောင့်ပိတ်ထားပါသည်' : 'Account Disabled'),
      message:
        customMessage ||
        (i18n.language === 'mm'
          ? 'သင့် Merchant အကောင့်ကို ပိတ်ထားပါသည်။ အကူအညီအတွက် ကျေးဇူးပြု၍ ဝန်ဆောင်မှုအဖွဲ့ကို ဆက်သွယ်ပါ။\n\nသင့်အသုံးပြုသူအကောင့်သို့ ပြန်သွားရန် Back to User ကိုနှိပ်ပါ။'
          : 'Your merchant account has been disabled. Please contact support for assistance.\n\nClick Back to User to return to your user account.'),
      confirmText: i18n.language === 'mm' ? 'အသုံးပြုသူအကောင့်သို့ပြန်သွားမည်' : 'Back to User',
      cancelText: i18n.language === 'mm' ? 'ပိတ်မည်' : 'Close',
    };
  };

  const content = getModalContent();

  if (!isOpen) return null;

  const dialogContent = (
    <div className="fixed inset-0 z-[100] transition-all duration-300 ease-out will-change-transform transform-gpu">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="bg-white/95 backdrop-blur-md border border-white/20 shadow-2xl rounded-2xl p-4 sm:p-6 w-full mx-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              {content.icon}
            </div>
            <h3 className="text-xl font-semibold mb-2 text-red-600">{content.title}</h3>
            <p className="text-gray-600 mb-6 whitespace-pre-line">{content.message}</p>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="flex-1 bg-gray-600 text-white py-3 rounded-xl font-medium hover:bg-gray-700 transition-colors"
              >
                {content.cancelText}
              </button>
              <button
                onClick={onConfirm}
                className="flex-1 bg-red-600 text-white py-3 rounded-xl font-medium hover:bg-red-700 transition-colors"
              >
                {content.confirmText}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(dialogContent, document.body);
}
